import React, { useEffect, useState } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import { getProjectCalls, scheduleCall, rescheduleCall, cancelCall, skipCall } from '../../services/pocCallService';
import { useToast, useConfirm } from '../../context/ToastContext';
import styles from './PocCallCard.module.css';

const formatIST = (iso) =>
  new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    dateStyle: 'medium',
    timeStyle: 'short',
  });

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  skipped: 'Skipped',
  cancelled: 'Cancelled — pending again',
};

// Mid-program and end-of-project calls (POC owner only). Book a 30-min Google
// Calendar call with a Meet link, or skip the milestone. If the student's
// email can't be found in SYNC nothing is booked: a modal explains why and the
// milestone stays pending.
const PocCallCard = ({ projectId, onEndCallChanged }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [slots, setSlots] = useState({});
  const [editing, setEditing] = useState({});
  const [busy, setBusy] = useState(null);
  const [missingModal, setMissingModal] = useState(null);

  const load = async () => {
    try {
      setItems(await getProjectCalls(projectId));
    } catch (error) {
      if (error.response?.status !== 403) console.error('Error fetching POC calls:', error);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const setSlot = (milestone, field, value) =>
    setSlots((prev) => ({ ...prev, [milestone]: { ...prev[milestone], [field]: value } }));

  const run = async (milestone, action, fn, successMsg) => {
    setBusy(`${milestone}:${action}`);
    try {
      await fn();
      toast.success(successMsg);
      setEditing((prev) => ({ ...prev, [milestone]: false }));
      await load();
      if (milestone === 'end') onEndCallChanged?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.code === 'missing_participants') {
        setMissingModal(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Something went wrong');
      }
    } finally {
      setBusy(null);
    }
  };

  const handleSchedule = (milestone, isReschedule) => {
    const { date, time } = slots[milestone] || {};
    if (!date || !time) {
      toast.error('Pick a date and a time');
      return;
    }
    if (isReschedule) {
      run(milestone, 'schedule', () => rescheduleCall(projectId, milestone, date, time), 'Call rescheduled');
    } else {
      run(milestone, 'schedule', () => scheduleCall(projectId, milestone, date, time), 'Call scheduled');
    }
  };

  const handleSkip = async (milestone, label) => {
    const ok = await confirm(`Skip the ${label.toLowerCase()} for this project? Its reminders will stop.`, {
      confirmText: 'Skip',
      variant: 'warning',
    });
    if (ok) run(milestone, 'skip', () => skipCall(projectId, milestone), 'Call skipped');
  };

  const handleCancel = async (milestone) => {
    const ok = await confirm('Cancel this call? Everyone invited will get a cancellation.', {
      confirmText: 'Cancel call',
      variant: 'danger',
    });
    if (ok) run(milestone, 'cancel', () => cancelCall(projectId, milestone), 'Call cancelled');
  };

  const visible = items.filter((item) => item.reached || item.call);
  if (visible.length === 0) return null;

  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

  const renderSlotForm = (milestone, isReschedule) => (
    <div className={styles.slotForm}>
      <div className={styles.field}>
        <label htmlFor={`call-date-${milestone}`}>Date</label>
        <input
          id={`call-date-${milestone}`}
          type="date"
          min={today}
          value={slots[milestone]?.date || ''}
          onChange={(e) => setSlot(milestone, 'date', e.target.value)}
        />
      </div>
      <div className={styles.field}>
        <label htmlFor={`call-time-${milestone}`}>Time (IST)</label>
        <input
          id={`call-time-${milestone}`}
          type="time"
          value={slots[milestone]?.time || ''}
          onChange={(e) => setSlot(milestone, 'time', e.target.value)}
        />
      </div>
      <Button onClick={() => handleSchedule(milestone, isReschedule)} loading={busy === `${milestone}:schedule`}>
        {isReschedule ? 'Save new time' : 'Schedule call'}
      </Button>
    </div>
  );

  return (
    <Card className={styles.card}>
      <h2>Calls with student</h2>
      <p className={styles.hint}>30-minute Google Meet call. The student and parent from SYNC are invited automatically.</p>

      {visible.map(({ milestone, label, call }) => {
        const status = call?.status;
        const pending = !status || status === 'cancelled';
        return (
          <div key={milestone} className={styles.milestone}>
            <div className={styles.milestoneHeader}>
              <h3>{label}</h3>
              <span className={`${styles.badge} ${styles[status || 'pending']}`}>
                {STATUS_LABELS[status] || 'Pending'}
              </span>
            </div>

            {pending && (
              <>
                {renderSlotForm(milestone, false)}
                <button
                  type="button"
                  className={styles.skipLink}
                  onClick={() => handleSkip(milestone, label)}
                  disabled={!!busy}
                >
                  Skip this time
                </button>
              </>
            )}

            {status === 'scheduled' && (
              <>
                <div className={styles.details}>
                  <div><strong>When</strong><span>{formatIST(call.scheduled_start)} IST</span></div>
                  {call.meet_link && (
                    <div>
                      <strong>Meet</strong>
                      <a href={call.meet_link} target="_blank" rel="noreferrer">{call.meet_link}</a>
                    </div>
                  )}
                  <div><strong>Invited</strong><span>{(call.participant_emails || []).join(', ')}</span></div>
                </div>
                {editing[milestone] ? (
                  renderSlotForm(milestone, true)
                ) : (
                  <div className={styles.actions}>
                    <Button variant="outline" size="small" onClick={() => setEditing((p) => ({ ...p, [milestone]: true }))}>
                      Reschedule
                    </Button>
                    <Button
                      variant="danger"
                      size="small"
                      onClick={() => handleCancel(milestone)}
                      loading={busy === `${milestone}:cancel`}
                    >
                      Cancel call
                    </Button>
                  </div>
                )}
              </>
            )}

            {status === 'skipped' && (
              <p className={styles.hint}>Skipped on {formatIST(call.acted_at)} IST.</p>
            )}
          </div>
        );
      })}

      <Modal isOpen={!!missingModal} onClose={() => setMissingModal(null)} title="Can't schedule this call" size="small">
        <p>{missingModal?.message}</p>
        <p className={styles.hint}>
          No call was booked and this milestone is still pending. Once the project is linked to its SYNC
          group and the student's email is on file there, try again.
        </p>
        <div className={styles.actions}>
          <Button onClick={() => setMissingModal(null)}>OK</Button>
        </div>
      </Modal>
    </Card>
  );
};

export default PocCallCard;
